import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { qk } from '@/lib/queryKeys.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { ConfirmDialog } from '@/components/ConfirmDialog.jsx';
import { Pencil, Trash2, Plus } from 'lucide-react';

const DAY_TYPE_OPTIONS = [
  { value: 'working', label: 'Working Day' },
  { value: 'half_day', label: 'Half Day' },
  { value: 'holiday', label: 'Holiday' },
];

const OT_TYPE_OPTIONS = [
  { value: 'single', label: 'Single OT' },
  { value: 'double', label: 'Double OT' },
  { value: 'triple', label: 'Triple OT' },
];

function ShiftFormDialog({ open, onClose, initialData }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const isEdit = !!initialData?.id;

  const form = useForm({
    defaultValues: initialData ?? {
      name: '',
      time_in: '08:00',
      time_out: '17:00',
      grace_in_minutes: 15,
      grace_out_minutes: 15,
      half_day_time_in: '',
      half_day_time_out: '',
      ot_start_time: '',
      early_ot_time_in: '',
      late_ot_time_out: '',
      saturday_type: 'working',
      saturday_time_in: '',
      saturday_time_out: '',
      saturday_ot_start: '',
      saturday_ot_type: 'double',
      sunday_type: 'holiday',
      sunday_time_in: '',
      sunday_time_out: '',
      sunday_ot_start: '',
      sunday_ot_type: 'double',
      weekday_ot_type: 'single',
      holiday_ot_type: 'double',
      short_leave_enabled: false,
      lunch_hours: 0,
      lunch_start_time: '',
      lunch_end_time: '',
      late_deduction_enabled: false,
      auto_ot_enabled: false,
      active: true,
    },
  });

  const satType = useWatch({ control: form.control, name: 'saturday_type' });
  const sunType = useWatch({ control: form.control, name: 'sunday_type' });

  const save = useMutation({
    mutationFn: (v) => {
      const payload = {
        ...v,
        grace_in_minutes: Number(v.grace_in_minutes),
        grace_out_minutes: Number(v.grace_out_minutes),
        half_day_time_in: v.half_day_time_in || null,
        half_day_time_out: v.half_day_time_out || null,
        ot_start_time: v.ot_start_time || null,
        early_ot_time_in: v.early_ot_time_in || null,
        late_ot_time_out: v.late_ot_time_out || null,
        saturday_time_in: v.saturday_type !== 'holiday' ? (v.saturday_time_in || null) : null,
        saturday_time_out: v.saturday_type !== 'holiday' ? (v.saturday_time_out || null) : null,
        saturday_ot_start: v.saturday_type !== 'holiday' ? (v.saturday_ot_start || null) : null,
        saturday_ot_type: v.saturday_ot_type || null,
        sunday_time_in: v.sunday_type !== 'holiday' ? (v.sunday_time_in || null) : null,
        sunday_time_out: v.sunday_type !== 'holiday' ? (v.sunday_time_out || null) : null,
        sunday_ot_start: v.sunday_type !== 'holiday' ? (v.sunday_ot_start || null) : null,
        sunday_ot_type: v.sunday_ot_type || null,
        weekday_ot_type: v.weekday_ot_type || null,
        holiday_ot_type: v.holiday_ot_type || null,
        late_deduction_enabled: v.late_deduction_enabled ? true : false,
        auto_ot_enabled: v.auto_ot_enabled ? true : false,
        short_leave_enabled: v.short_leave_enabled ? true : false,
        lunch_hours: Number(v.lunch_hours) || 0,
        lunch_start_time: v.lunch_start_time || null,
        lunch_end_time: v.lunch_end_time || null,
        active: v.active ? true : false,
      };
      return isEdit ? api.put(`/shifts/${initialData.id}`, payload) : api.post('/shifts', payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.shifts });
      toast({ variant: 'success', title: isEdit ? 'Shift updated' : 'Shift created' });
      onClose();
    },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Shift' : 'New Shift'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="space-y-5">
          {/* Basic */}
          <Section title="Basic">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Name" required className="md:col-span-2">
                <Input {...form.register('name', { required: true })} />
              </FormField>
              <FormField label="Active">
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="shift-active" {...form.register('active')} className="h-4 w-4 rounded border-slate-300" />
                  <label htmlFor="shift-active" className="text-sm text-slate-700 dark:text-slate-300">Active</label>
                </div>
              </FormField>
              <FormField label="Late Deduction">
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="shift-late-ded" {...form.register('late_deduction_enabled')} className="h-4 w-4 rounded border-slate-300" />
                  <label htmlFor="shift-late-ded" className="text-sm text-slate-700 dark:text-slate-300">Enable Late Hours Deduction</label>
                </div>
              </FormField>
              <FormField label="Auto OT">
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="shift-auto-ot" {...form.register('auto_ot_enabled')} className="h-4 w-4 rounded border-slate-300" />
                  <label htmlFor="shift-auto-ot" className="text-sm text-slate-700 dark:text-slate-300">Enable Automatic OT Calculation</label>
                </div>
              </FormField>
              <FormField label="Short Leave">
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="shift-sl" {...form.register('short_leave_enabled')} className="h-4 w-4 rounded border-slate-300" />
                  <label htmlFor="shift-sl" className="text-sm text-slate-700 dark:text-slate-300">Enable Short Leave Forgiveness</label>
                </div>
              </FormField>
            </div>
          </Section>

          {/* Main Schedule */}
          <Section title="Main Schedule">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Time In" required>
                <Input type="time" {...form.register('time_in', { required: true })} />
              </FormField>
              <FormField label="Time Out" required>
                <Input type="time" {...form.register('time_out', { required: true })} />
              </FormField>
              <FormField label="Grace In (minutes)">
                <Input type="number" min={0} {...form.register('grace_in_minutes', { valueAsNumber: true })} />
              </FormField>
              <FormField label="Grace Out (minutes)">
                <Input type="number" min={0} {...form.register('grace_out_minutes', { valueAsNumber: true })} />
              </FormField>
            </div>
          </Section>

          {/* Half Day Times */}
          <Section title="Half Day Times">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Half Day Time In">
                <Input type="time" {...form.register('half_day_time_in')} />
              </FormField>
              <FormField label="Half Day Time Out">
                <Input type="time" {...form.register('half_day_time_out')} />
              </FormField>
            </div>
          </Section>

          {/* Lunch Break */}
          <Section title="Lunch Break">
            <div className="grid gap-4 md:grid-cols-3">
              <FormField label="Lunch Hours" hint="Deducted from expected paid hours">
                <Input type="number" step="0.25" min={0} max={24} {...form.register('lunch_hours', { valueAsNumber: true })} />
              </FormField>
              <FormField label="Lunch Start" hint="Optional — for overlap calculation">
                <Input type="time" {...form.register('lunch_start_time')} />
              </FormField>
              <FormField label="Lunch End" hint="Optional — for overlap calculation">
                <Input type="time" {...form.register('lunch_end_time')} />
              </FormField>
            </div>
          </Section>

          {/* Overtime */}
          <Section title="Overtime">
            <div className="grid gap-4 md:grid-cols-3">
              <FormField label="OT Start Time">
                <Input type="time" {...form.register('ot_start_time')} />
              </FormField>
              <FormField label="Early OT Time In">
                <Input type="time" {...form.register('early_ot_time_in')} />
              </FormField>
              <FormField label="Late OT Time Out">
                <Input type="time" {...form.register('late_ot_time_out')} />
              </FormField>
              <FormField label="Weekday OT Type" hint="Multiplier for weekday extra hours">
                <Select {...form.register('weekday_ot_type')}>
                  <option value="">— use company default —</option>
                  {OT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
              <FormField label="Holiday OT Type" hint="Multiplier on public holiday attendance">
                <Select {...form.register('holiday_ot_type')}>
                  <option value="">— use company default —</option>
                  {OT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
            </div>
          </Section>

          {/* Saturday Rules */}
          <Section title="Saturday Rules">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Saturday Type">
                <Select {...form.register('saturday_type')}>
                  {DAY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
              <FormField label="Saturday OT Type">
                <Select {...form.register('saturday_ot_type')}>
                  <option value="">— use company default —</option>
                  {OT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
              {(satType === 'working' || satType === 'half_day') && (
                <>
                  <FormField label="Saturday Time In">
                    <Input type="time" {...form.register('saturday_time_in')} />
                  </FormField>
                  <FormField label="Saturday Time Out">
                    <Input type="time" {...form.register('saturday_time_out')} />
                  </FormField>
                  <FormField label="Saturday OT Start">
                    <Input type="time" {...form.register('saturday_ot_start')} />
                  </FormField>
                </>
              )}
            </div>
          </Section>

          {/* Sunday Rules */}
          <Section title="Sunday Rules">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Sunday Type">
                <Select {...form.register('sunday_type')}>
                  {DAY_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
              <FormField label="Sunday OT Type">
                <Select {...form.register('sunday_ot_type')}>
                  <option value="">— use company default —</option>
                  {OT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
              {(sunType === 'working' || sunType === 'half_day') && (
                <>
                  <FormField label="Sunday Time In">
                    <Input type="time" {...form.register('sunday_time_in')} />
                  </FormField>
                  <FormField label="Sunday Time Out">
                    <Input type="time" {...form.register('sunday_time_out')} />
                  </FormField>
                  <FormField label="Sunday OT Start">
                    <Input type="time" {...form.register('sunday_ot_start')} />
                  </FormField>
                </>
              )}
            </div>
          </Section>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Shift'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ShiftsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: qk.shifts,
    queryFn: () => api.get('/shifts'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.del(`/shifts/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.shifts });
      toast({ variant: 'success', title: 'Shift deleted' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Delete failed', description: e.message }),
  });

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row) => { setEditing(row); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); };

  const columns = [
    { key: 'name', header: 'Name', sortable: true },
    { key: 'time_in', header: 'Time In', render: (r) => r.time_in },
    { key: 'time_out', header: 'Time Out', render: (r) => r.time_out },
    {
      key: 'saturday_type', header: 'Saturday',
      render: (r) => DAY_TYPE_OPTIONS.find((o) => o.value === r.saturday_type)?.label ?? r.saturday_type,
    },
    {
      key: 'sunday_type', header: 'Sunday',
      render: (r) => DAY_TYPE_OPTIONS.find((o) => o.value === r.sunday_type)?.label ?? r.sunday_type,
    },
    { key: 'employee_count', header: 'Employees', render: (r) => r.employee_count ?? 0 },
    {
      key: 'auto_ot_enabled', header: 'Auto OT',
      render: (r) => r.auto_ot_enabled ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">On</span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">Off</span>
      ),
    },
    {
      key: 'late_deduction_enabled', header: 'Late Ded.',
      render: (r) => r.late_deduction_enabled ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">On</span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">Off</span>
      ),
    },
    {
      key: 'active', header: 'Active',
      render: (r) => r.active ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Yes</span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">No</span>
      ),
    },
    {
      key: '_actions', header: '',
      render: (r) => (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(r)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Shifts"
        description="Work shift schedules"
        actions={
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />New Shift</Button>
        }
      />

      <DataTable
        columns={columns}
        data={shifts}
        loading={isLoading}
        exportFilename="shifts.csv"
        emptyMessage="No shifts defined"
      />

      {formOpen && (
        <ShiftFormDialog
          open={formOpen}
          onClose={closeForm}
          initialData={editing}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Shift"
        description={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={() => { remove.mutate(deleteTarget.id); setDeleteTarget(null); }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
